import { Checkbox, Input } from "@mui/joy";
import React, { SetStateAction, Dispatch, useEffect } from "react";
import { BLACK, WHITE } from "../../appConstants";

type SuggestPrincipal = {
  label: string;
  id: string;
};

type MultiSelectSearchComponentProps = {
  InputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  InputValue: string;
  setInputValue: Dispatch<SetStateAction<string>>;
  onKeyDownHandler: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  FilterFocus: boolean;
  setFilterFocus: Dispatch<SetStateAction<boolean>>;
  selectedPriciplesValue: string;
  filterPrinciples: SuggestPrincipal[];
  setFilterPrinciples: Dispatch<SetStateAction<SuggestPrincipal[]>>;
  filtersearchPrinciples: SuggestPrincipal[];
  selectedPrinciples: Set<string>;
  setSelectedValue: Dispatch<SetStateAction<string>>;
  setSelectedPrinciples: Dispatch<SetStateAction<Set<string>>>;
};

const MultiSelectSearchComponent: React.FC<MultiSelectSearchComponentProps> = ({
  InputChange,
  InputValue,
  setInputValue,
  onKeyDownHandler,
  FilterFocus,
  setFilterFocus,
  selectedPriciplesValue,
  filterPrinciples,
  setFilterPrinciples,
  filtersearchPrinciples,
  selectedPrinciples,
  setSelectedPrinciples,
  setSelectedValue,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const onPrincipleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    let set = new Set(selectedPrinciples);
    if (e.target.checked) {
      set.add(id);
    } else {
      if (set.has(id)) {
        set.delete(id);
      }
    }
    const selectedCategories = filtersearchPrinciples
      .filter((principle) => set.has(principle.id)) // Filter based on selected ids
      .map((principle) => principle.label); // Get the label values

    // Join the categories with commas
    const categoriesString = selectedCategories.join(", ");

    // Update the selectedPrinciplesValue state
    setSelectedValue(categoriesString);
    setSelectedPrinciples(set);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setFilterFocus(false);
        setInputValue("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const setFilterFocusHandler = (val: boolean) => {
    setFilterFocus(val);
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex",
        position: "relative",
        flexDirection: "row",
        alignItems: "center",
      }}
    >
      <Input
        onChange={InputChange}
        onClick={() => {
          if (selectedPrinciples.size === 0) {
            setFilterPrinciples(filtersearchPrinciples);
          } else {
            const sortedData = filtersearchPrinciples.sort((a, b) => {
              // Check if the id is in the set
              const aInSet = selectedPrinciples.has(a.id) ? 0 : 1;
              const bInSet = selectedPrinciples.has(b.id) ? 0 : 1;

              // Sort by presence in set
              return aInSet - bInSet;
            });
            setFilterPrinciples(sortedData);
          }
        }}
        onFocus={() => setFilterFocusHandler(true)}
        value={FilterFocus ? InputValue : ""}
        ref={inputRef}
        onKeyDown={onKeyDownHandler}
        placeholder={
          selectedPriciplesValue && FilterFocus === false
            ? selectedPriciplesValue
            : "Search Value"
        }
        endDecorator={
          FilterFocus ? (
            <svg
              onClick={() => setFilterFocusHandler(false)}
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.99984 9L6.74984 12.25C6.59706 12.4028 6.40262 12.4792 6.1665 12.4792C5.93039 12.4792 5.73595 12.4028 5.58317 12.25C5.43039 12.0972 5.354 11.9028 5.354 11.6667C5.354 11.4306 5.43039 11.2361 5.58317 11.0833L9.4165 7.25C9.58317 7.08333 9.77762 7 9.99984 7C10.2221 7 10.4165 7.08333 10.5832 7.25L14.4165 11.0833C14.5693 11.2361 14.6457 11.4306 14.6457 11.6667C14.6457 11.9028 14.5693 12.0972 14.4165 12.25C14.2637 12.4028 14.0693 12.4792 13.8332 12.4792C13.5971 12.4792 13.4026 12.4028 13.2498 12.25L9.99984 9Z"
                fill="#1C1C1C"
                fill-opacity="0.4"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="cursor-pointer"
              onClick={() => setFilterFocusHandler(true)}
            >
              <path
                d="M10.0001 12.4792C9.88897 12.4792 9.7848 12.4618 9.68758 12.4271C9.59036 12.3924 9.50008 12.3333 9.41675 12.25L5.58341 8.41668C5.43064 8.2639 5.35425 8.06945 5.35425 7.83334C5.35425 7.59723 5.43064 7.40279 5.58341 7.25001C5.73619 7.09723 5.93064 7.02084 6.16675 7.02084C6.40286 7.02084 6.5973 7.09723 6.75008 7.25001L10.0001 10.5L13.2501 7.25001C13.4029 7.09723 13.5973 7.02084 13.8334 7.02084C14.0695 7.02084 14.264 7.09723 14.4167 7.25001C14.5695 7.40279 14.6459 7.59723 14.6459 7.83334C14.6459 8.06945 14.5695 8.2639 14.4167 8.41668L10.5834 12.25C10.5001 12.3333 10.4098 12.3924 10.3126 12.4271C10.2154 12.4618 10.1112 12.4792 10.0001 12.4792Z"
                fill="#1C1C1C"
                fill-opacity="0.4"
              />
            </svg>
          )
        }
        sx={{
          // height: "54px",

          width: "100%",
          borderBottom: FilterFocus ? "0px" : `1px solid ${BLACK.B_10}`,
          borderTop: `1px solid ${BLACK.B_10}`,
          borderLeft: `1px solid ${BLACK.B_10}`,
          borderRight: `1px solid ${BLACK.B_10}`,
          borderRadius: "8px",
          backgroundColor: WHITE.W_100,
          paddingLeft: "16px",
          paddingRight: "16px",
          paddingTop: "20px",
          paddingBottom: "20px",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "20px",
          boxShadow: "none",
          color:
            selectedPriciplesValue !== "Search Value"
              ? BLACK.B_100
              : BLACK.B_40,
        }}
      />
      {FilterFocus && (
        <div
          className="rounded-[8px]"
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: 62,
            left: 0,
            width: "100%",
            zIndex: 9999,
            borderBottomRightRadius: "6px",
            borderBottomLeftRadius: "6px",
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            borderBottom: `1px solid ${BLACK.B_10}`,
            borderLeft: `1px solid ${BLACK.B_10}`,
            borderRight: `1px solid ${BLACK.B_10}`,
            backgroundColor: WHITE.W_100,
            boxShadow: `0px 4px 10px 4px rgba(130, 130, 130, 0.10)`,
          }}
        >
          <div
            className="py-2 rounded-[8px]"
            style={{
              width: "100%",
              flex: 1,
              maxHeight: 200,
              overflow: "auto",
              backgroundColor: "white",
            }}
          >
            {filterPrinciples.length > 0 &&
              filterPrinciples.map((p) => {
                return (
                  <div
                    key={p?.id}
                    className="flex items-center px-4 py-2 gap-1 self-stretch"
                    style={{ height: 34 }}
                  >
                    <Checkbox
                      size="sm"
                      onChange={(e) => onPrincipleChange(e, p?.id)}
                      checked={selectedPrinciples?.has(p?.id)}
                      color={"neutral"}
                    />
                    <span
                      className="ml-2"
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "20px",
                        color: BLACK.B_80,
                      }}
                    >
                      {p?.label}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectSearchComponent;
